import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSubscriptionStatus, getSubscriptionPlans, createCheckoutSession, cancelSubscription } from "../../../../redux/reducers/subscriptionSlice";
import { Badge, Card, Row, Col, Button, Modal } from "react-bootstrap";
import Spinner from "../../../../components/Spinner";
import { LuCircleCheck, LuZap, LuRocket, LuGem, LuCircleAlert, LuCircleX } from "react-icons/lu";
import ConfirmModal from "../../../../components/ConfirmModal";

export default function Subscription() {
  const dispatch = useDispatch();
  const { status, plans, loading } = useSelector((state) => state.subscription);
  const { userDetail } = useSelector((state) => state.auth);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    dispatch(getSubscriptionStatus());
    dispatch(getSubscriptionPlans());
  }, [dispatch]);

  useEffect(() => {
    if (status?.plan?._id) {
      setSelectedPlanId(status.plan._id);
    }
  }, [status]);

  const handlePlanSelect = (planId) => {
    dispatch(createCheckoutSession(planId));
  };

  const handleUpdateSubscription = () => {
    if (selectedPlanId && selectedPlanId !== status?.plan?._id) {
      dispatch(createCheckoutSession(selectedPlanId));
      setShowUpdateModal(false);
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const getPlanIcon = (planName) => {
    const name = planName?.toLowerCase();
    if (name?.includes("pro")) return <LuRocket size={24} className="text-brand" />;
    if (name?.includes("enterprise")) return <LuGem size={24} className="text-purple" />;
    return <LuZap size={24} className="text-warning" />;
  };

  const hasActivePlan = !!status?.plan;

  return (
    <Spinner active={loading}>
      <div className="py-2">
        {/* Current Plan Overview */}
        <Card className="border-0 shadow-sm rounded-4 mb-5 overflow-hidden">
          <Card.Header className="bg-light border-0 py-3 px-4 d-flex justify-content-between align-items-center">
            <div>
              <h5 className="fw-bold mb-0 text-brand">Current Plan</h5>
              <p className="text-muted small mb-0">Manage your active subscription details</p>
            </div>
            {hasActivePlan ? (
              <Badge bg="soft-success" className="text-success rounded-pill px-3 py-2">
                Active
              </Badge>
            ) : (
              <Badge bg="soft-warning" className="text-warning rounded-pill px-3 py-2">
                No Active Plan
              </Badge>
            )}
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="align-items-center">
              <Col md={7}>
                <h4 className="fw-bold text-dark mb-1">
                  {status?.plan?.name || "Free Tier / Trial"}
                </h4>
                <p className="text-muted mb-3">
                  {status?.plan?.description || "You are currently using the default limited access tier."}
                </p>
                <div className="d-flex gap-4">
                  <div>
                    <div className="text-muted smaller text-uppercase fw-bold mb-1">Next Billing</div>
                    <div className="fw-bold">{status?.nextBillingDate ? new Date(status.nextBillingDate).toLocaleDateString() : "N/A"}</div>
                  </div>
                  <div>
                    <div className="text-muted smaller text-uppercase fw-bold mb-1">Price</div>
                    <div className="fw-bold">{status?.plan?.price ? `$${status.plan.price}/mo` : "Free"}</div>
                  </div>
                </div>
              </Col>
              <Col md={5} className="text-md-end mt-3 mt-md-0 d-flex flex-column gap-2 align-items-md-end">
                <Button 
                  variant="brand" 
                  className="rounded-pill px-4 fw-bold shadow-sm w-fit-content"
                  onClick={() => setShowUpdateModal(true)}
                >
                  Update Subscription
                </Button>
                {hasActivePlan && (
                  <Button 
                    variant="link" 
                    className="text-danger p-0 text-decoration-none fw-semibold small mt-1"
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    style={{ fontSize: '12px' }}
                  >
                    Cancel Subscription
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Available Plans Selection */}
        <div className="mb-4">
          <h4 className="fw-bold text-dark mb-1">Available Plans</h4>
          <p className="text-muted">Choose the best plan for your business growth</p>
        </div>

        <Row className="g-4">
          {plans?.map((plan) => {
            const isCurrent = status?.plan?._id === plan._id;
            return (
              <Col key={plan._id} lg={4} md={6}>
                <div className={`pricing-card h-100 p-4 rounded-4 border-2 transition-all ${isCurrent ? 'border-brand' : 'border-light bg-white shadow-sm'}`} 
                     style={{ position: 'relative', transition: 'transform 0.2s' }}>
                  {isCurrent && (
                    <div className="position-absolute top-0 start-50 translate-middle">
                      <Badge bg="brand" className="rounded-pill px-3 py-2 shadow-sm text-white fw-bold">Current Plan</Badge>
                    </div>
                  )}
                  
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="p-3 bg-light rounded-4">
                      {getPlanIcon(plan.name)}
                    </div>
                    <div>
                      <h5 className="fw-bold mb-0">{plan.name}</h5>
                      <div className="text-brand fs-4 fw-bold">${plan.price}<span className="text-muted small fw-normal">/mo</span></div>
                    </div>
                  </div>

                  <ul className="list-unstyled mb-5">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-center gap-2 mb-3 text-muted small">
                        <LuCircleCheck size={16} className="text-brand flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    variant={isCurrent ? "outline-secondary" : "brand"}
                    className="w-100 rounded-pill py-2 fw-bold"
                    disabled={isCurrent || loading}
                    onClick={() => handlePlanSelect(plan._id)}
                  >
                    {isCurrent ? "Active" : hasActivePlan ? "Update Plan" : "Select Plan"}
                  </Button>
                </div>
              </Col>
            );
          })}
          
          {/* No Plans Fallback */}
          {!loading && plans?.length === 0 && (
            <Col xs={12}>
              <div className="text-center py-5 bg-light rounded-4 border border-dashed">
                <LuCircleAlert size={40} className="text-muted mb-3 opacity-50" />
                <h5 className="fw-bold text-muted">No additional plans available at this moment.</h5>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* ── Update Subscription Modal ── */}
      <Modal 
        show={showUpdateModal} 
        onHide={() => setShowUpdateModal(false)} 
        centered 
        className="premium-modal"
        contentClassName="rounded-4 border-0 shadow"
      >
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <Modal.Title className="fw-bold h4 mb-0">Update Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
            You are currently on the <span className="fw-bold text-dark">{status?.plan?.name || "Free"}</span> plan. Select a new tier below.
          </p>
          
          <div className="d-flex flex-column gap-2 mb-4">
            {plans?.map((plan) => {
              const isSelected = selectedPlanId === plan._id;
              const isCurrent = status?.plan?._id === plan._id;

              return (
                <div 
                  key={plan._id}
                  className={`p-3 rounded-3 cursor-pointer d-flex align-items-center justify-content-between transition-all ${
                    isSelected ? 'bg-dark text-white border-dark' : 'bg-light text-dark border-transparent'
                  }`}
                  style={{ 
                    cursor: isCurrent ? 'default' : 'pointer',
                    border: '2px solid transparent',
                    opacity: isCurrent ? 0.8 : 1
                  }}
                  onClick={() => !isCurrent && setSelectedPlanId(plan._id)}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div className={`rounded-circle d-flex align-items-center justify-content-center ${isSelected ? 'bg-white text-dark' : 'bg-white text-brand'}`} style={{ width: '20px', height: '20px' }}>
                      {isSelected && <LuCircleCheck size={14} />}
                    </div>
                    <div>
                      <div className={`fw-bold ${isSelected ? 'text-white' : 'text-dark'}`} style={{ fontSize: '14px' }}>
                        {plan.name} - {plan.description || "All features included"}
                      </div>
                    </div>
                  </div>
                  {isCurrent && <Badge bg="success" className="rounded-pill px-2">Current</Badge>}
                </div>
              );
            })}
          </div>

          <div className="d-flex gap-2">
            <Button 
              variant="light" 
              className="w-100 rounded-pill fw-bold py-2"
              onClick={() => setShowUpdateModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="brand" 
              className="w-100 rounded-pill fw-bold py-2 shadow-sm"
              disabled={loading || selectedPlanId === status?.plan?._id}
              onClick={handleUpdateSubscription}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <ConfirmModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={() => {
            dispatch(cancelSubscription());
            setShowCancelModal(false);
        }}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? This action will take effect at the end of your current billing cycle."
        confirmText="Yes, Cancel"
        variant="danger"
        icon={LuCircleX}
      />
    </Spinner>
  );
}
